import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { MediaKitPreview } from "@/components/mediakit/MediaKitPreview";
import { Loader2, Megaphone, Users, MapPin, Store, ShieldCheck, History, Search, FileText, ExternalLink, Instagram, ChevronDown, ChevronUp, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "draft", label: "Draft" },
  { key: "live", label: "Live" },
  { key: "completed", label: "Completed" },
];

function statusChip(o) {
  const live = o.status === "published";
  const completed = o.status === "closed" || o.event_status === "completed";
  if (completed) return { text: "Completed", cls: "bg-zinc-200 text-zinc-700" };
  if (live) return { text: "Live", cls: "bg-emerald-100 text-emerald-700" };
  if (o.approval_state === "awaiting_business_approval") return { text: "Draft · awaiting approval", cls: "bg-amber-100 text-amber-700" };
  return { text: "Draft", cls: "bg-zinc-100 text-zinc-600" };
}
const visLabel = (v) => (v === "direct" ? "Invite only" : v === "both" ? "Public + invites" : "Public");
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return "—"; } };
// UI status <-> backend status
const UI_TO_STATUS = { draft: "draft", live: "published", completed: "closed" };
const statusToUi = (o) => (o.status === "published" ? "live" : (o.status === "closed" || o.event_status === "completed") ? "completed" : "draft");

const APP_STATUS_TONE = {
  applied: "bg-amber-100 text-amber-700", under_review: "bg-indigo-100 text-indigo-700",
  shortlisted: "bg-purple-100 text-purple-700", waitlisted: "bg-amber-100 text-amber-700",
  accepted: "bg-emerald-100 text-emerald-700", declined: "bg-zinc-200 text-zinc-600",
  invited: "bg-sky-100 text-sky-700",
};
const APP_STATUS_FILTERS = ["all", "applied", "under_review", "shortlisted", "waitlisted", "accepted", "declined", "invited"];
const fmtDateTime = (s) => { try { return new Date(s).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

function ApplicantCard({ a, oppId, onInvestigate }) {
  const [expanded, setExpanded] = useState(false);
  const [kitOpen, setKitOpen] = useState(false);
  const [kit, setKit] = useState(null);
  const [kitBusy, setKitBusy] = useState(false);
  const viewKit = async () => {
    setKitBusy(true);
    try { const r = await api.get(`/admin/opportunities/${oppId}/applications/${a.application_id}/media-kit`); setKit(r.data.kit); setKitOpen(true); }
    catch { toast.error("Could not load this media kit"); }
    finally { setKitBusy(false); }
  };
  const tone = APP_STATUS_TONE[a.status] || "bg-zinc-100 text-zinc-600";
  return (
    <div data-testid={`admin-applicant-${a.application_id}`} className="rounded-2xl border border-zinc-200 bg-white p-3">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 overflow-hidden rounded-xl bg-zinc-100">
          {a.profile_photo ? <img src={fileUrl(a.profile_photo)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center font-display font-bold text-zinc-400">{a.creator_name?.[0]}</div>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <p className="truncate font-display font-semibold text-zinc-900">{a.creator_name}</p>
            {a.type === "invitation" && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">Invited</span>}
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold capitalize ${tone}`}>{a.status.replace(/_/g, " ")}</span>
          </div>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500">
            {a.instagram && <span className="inline-flex items-center gap-1"><Instagram className="h-3 w-3" /> @{a.instagram}</span>}
            <span>Applied {fmtDateTime(a.created_at)}</span>
          </p>
          <p className="mt-0.5 text-xs text-zinc-400">
            Business viewed: {a.viewed_by_business_at ? fmtDateTime(a.viewed_by_business_at) : <span className="italic">Not recorded</span>}
          </p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        <a data-testid={`admin-applicant-profile-${a.application_id}`} href={`/creators/${a.creator_user_id}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"><ExternalLink className="h-3 w-3" /> Profile</a>
        {onInvestigate && (a.instagram || a.creator_email) && (
          <button data-testid={`admin-applicant-investigate-${a.application_id}`}
            onClick={() => onInvestigate(a.instagram ? `@${a.instagram}` : a.creator_email)}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50"><ShieldCheck className="h-3 w-3" /> Investigate</button>
        )}
        <button data-testid={`admin-applicant-read-${a.application_id}`} onClick={() => setExpanded((e) => !e)}
          className="inline-flex items-center gap-1 rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50">
          {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Read application
        </button>
        {a.has_media_kit && (
          <button data-testid={`admin-applicant-kit-${a.application_id}`} onClick={viewKit} disabled={kitBusy}
            className="inline-flex items-center gap-1 rounded-full border border-emerald-200 px-3 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">
            {kitBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />} Media kit
          </button>
        )}
      </div>

      {expanded && (
        <div className="mt-2 space-y-2" data-testid={`admin-applicant-details-${a.application_id}`}>
          <div className="rounded-xl bg-zinc-50 p-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Application message</p>
            <p className="mt-1 whitespace-pre-line text-sm text-zinc-700">{a.message || <span className="italic text-zinc-400">No message provided.</span>}</p>
          </div>
          {a.status === "declined" && (a.decline_reason_label || a.decline_message) && (
            <div className="rounded-xl border border-zinc-200 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Rejection feedback</p>
              {a.decline_reason_label && <p className="mt-1 text-sm text-zinc-700"><b>Reason:</b> {a.decline_reason_label}</p>}
              {a.decline_message && <p className="mt-1 whitespace-pre-line text-sm text-zinc-700"><b>Message:</b> {a.decline_message}</p>}
            </div>
          )}
        </div>
      )}

      <Dialog open={kitOpen} onOpenChange={setKitOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid={`admin-kit-modal-${a.application_id}`}>
          <DialogHeader><DialogTitle>{a.creator_name} — media kit</DialogTitle><DialogDescription>Immutable snapshot attached to this application (read-only).</DialogDescription></DialogHeader>
          {kit ? <MediaKitPreview kit={kit} /> : <p className="text-sm text-zinc-500">No media kit to preview.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ApplicantsDialog({ opp, onClose, onInvestigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    if (!opp) return;
    setLoading(true); setData(null); setQ(""); setStatus("all");
    api.get(`/admin/opportunities/${opp.id}/applicants`).then((r) => setData(r.data))
      .catch(() => toast.error("Could not load applicants")).finally(() => setLoading(false));
  }, [opp]);

  const all = data?.applicants || [];
  const filtered = all.filter((a) => {
    if (status !== "all" && a.status !== status) return false;
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      return (a.creator_name || "").toLowerCase().includes(ql) || (a.instagram || "").toLowerCase().includes(ql);
    }
    return true;
  });

  return (
    <Dialog open={!!opp} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-applicants-dialog">
        <DialogHeader>
          <DialogTitle className="pr-6">Applicants</DialogTitle>
          <DialogDescription>{opp?.title} · {data ? `${data.applicant_count} applicant${data.applicant_count === 1 ? "" : "s"}` : "…"}</DialogDescription>
        </DialogHeader>

        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div> : (
          <>
            <div className="sticky top-0 z-10 space-y-2 bg-white pb-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="admin-applicants-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or @instagram" className="h-10 rounded-full pl-9" />
              </div>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="admin-applicants-status" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {APP_STATUS_FILTERS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.replace(/_/g, " ")}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {all.length === 0 ? (
              <div className="py-10 text-center" data-testid="admin-applicants-empty">
                <Users className="mx-auto h-8 w-8 text-zinc-300" />
                <p className="mt-2 text-sm text-zinc-500">No one has applied to this opportunity yet.</p>
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-400" data-testid="admin-applicants-none-match">No applicants match your search or filter.</p>
            ) : (
              <div className="space-y-2" data-testid="admin-applicants-list">
                {filtered.map((a) => <ApplicantCard key={a.application_id} a={a} oppId={opp.id} onInvestigate={onInvestigate} />)}
              </div>
            )}
          </>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-full">Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AdminOpportunities({ onInvestigate }) {
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [newStatus, setNewStatus] = useState("draft");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [applicantsOpp, setApplicantsOpp] = useState(null);
  const [nudgeOnly, setNudgeOnly] = useState(false);

  const load = async (f = filter) => {
    setLoading(true);
    try { const r = await api.get(`/admin/opportunities?filter=${f}`); setRows(r.data.opportunities || []); }
    catch { toast.error("Could not load opportunities"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const open = async (o) => {
    setDetail({ id: o.id }); setDetailLoading(true);
    try {
      const r = await api.get(`/admin/opportunities/${o.id}`);
      setDetail(r.data); setNewStatus(statusToUi(r.data)); setReason("");
    } catch { toast.error("Could not load opportunity"); setDetail(null); }
    finally { setDetailLoading(false); }
  };

  const applyStatus = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason for the change"); return; }
    setSaving(true);
    try {
      await api.post(`/admin/opportunities/${detail.id}/admin-status`, { status: UI_TO_STATUS[newStatus], reason: reason.trim() });
      toast.success("Status updated and recorded in the audit log");
      setDetail(null);
      load(filter);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status"); }
    finally { setSaving(false); }
  };

  const nudgeCount = rows.filter((o) => o.needs_nudge).length;
  const visibleRows = nudgeOnly ? rows.filter((o) => o.needs_nudge) : rows;

  return (
    <div className="mt-6">
      <div className="flex flex-wrap items-center gap-2" data-testid="admin-opp-filters">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`admin-opp-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${filter === f.key ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 border border-zinc-200"}`}>
            {f.label}
          </button>
        ))}
        <button data-testid="admin-opp-nudge-toggle" onClick={() => setNudgeOnly((v) => !v)}
          className={`ml-auto inline-flex items-center gap-1 rounded-full px-4 py-1.5 text-sm font-medium ${nudgeOnly ? "bg-amber-500 text-white" : "bg-white text-amber-700 border border-amber-200"}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Needs attention{nudgeCount ? ` · ${nudgeCount}` : ""}
        </button>
      </div>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        : visibleRows.length === 0 ? <p className="mt-6 text-sm text-zinc-400">{nudgeOnly ? "No opportunities need attention right now." : "No opportunities in this view."}</p>
        : (
          <div className="mt-4 space-y-2" data-testid="admin-opp-list">
            {visibleRows.map((o) => {
              const sc = statusChip(o);
              const applicants = o.applicant_count ?? o.application_count ?? 0;
              return (
                <div key={o.id} data-testid={`admin-opp-item-${o.id}`} role="button" tabIndex={0}
                  onClick={() => open(o)} onKeyDown={(e) => { if (e.key === "Enter") open(o); }}
                  className="flex w-full flex-wrap items-center gap-3 rounded-2xl border border-zinc-200 bg-white p-4 text-left transition-colors hover:border-emerald-300 cursor-pointer">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-zinc-100 text-zinc-400"><Megaphone className="h-4 w-4" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-zinc-900">{o.title || "Untitled"}</p>
                    <p className="truncate text-sm text-zinc-500">
                      <span className="inline-flex items-center gap-1"><Store className="h-3 w-3" /> {o.business_name}</span>
                      {" · "}{visLabel(o.visibility)}{" · "}{fmtDate(o.created_at)}
                    </p>
                  </div>
                  {o.needs_nudge && (
                    <span data-testid={`admin-opp-nudge-${o.id}`} className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700" title="Applicants waiting with no business view recorded">
                      <AlertTriangle className="h-3 w-3" /> {o.unactioned_applicants} waiting{o.oldest_unactioned_days != null ? ` ${o.oldest_unactioned_days}d` : ""}
                    </span>
                  )}
                  <span data-testid={`admin-opp-count-${o.id}`} className="inline-flex items-center gap-1 text-sm text-zinc-500"><Users className="h-3.5 w-3.5" /> {applicants}</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${sc.cls}`}>{sc.text}</span>
                  <button data-testid={`admin-opp-view-applicants-${o.id}`} onClick={(e) => { e.stopPropagation(); setApplicantsOpp({ id: o.id, title: o.title }); }}
                    className="rounded-full border border-zinc-200 px-3 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50">View applicants</button>
                </div>
              );
            })}
          </div>
        )}

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="admin-opp-detail">
          {detailLoading || !detail?.title ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="pr-6">{detail.title}</DialogTitle>
                <DialogDescription>{detail.business_name}{detail.business_email ? ` · ${detail.business_email}` : ""}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Status</span><span className="font-semibold capitalize text-zinc-900">{detail.status}{detail.approval_state === "awaiting_business_approval" ? " (awaiting approval)" : ""}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Visibility</span><span className="text-zinc-900">{visLabel(detail.visibility)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Created</span><span className="text-zinc-900">{fmtDate(detail.created_at)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Applications</span><span className="text-zinc-900">{detail.application_count}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Location</span><span className="text-zinc-900 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{detail.location || "—"}</span></div>
                {detail.admin_corrected && <div className="rounded-lg bg-indigo-50 px-2 py-1 text-xs text-indigo-700">This opportunity was previously corrected by an admin.</div>}
              </div>

              {detail.description && <p className="mt-2 whitespace-pre-line rounded-xl bg-zinc-50 p-3 text-sm text-zinc-700">{detail.description}</p>}

              {/* Admin status correction */}
              <div className="mt-3 rounded-xl border border-zinc-200 p-3">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-800"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Correct status (admin)</p>
                <div className="mt-2 grid gap-2">
                  <div>
                    <Label className="text-xs">New status</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="admin-opp-status-select" className="mt-1 h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Reason (recorded in the audit log) *</Label>
                    <Textarea data-testid="admin-opp-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-1" placeholder="e.g. Business confirmed publish by phone; fixing status after publishing bug." />
                  </div>
                </div>
              </div>

              {/* Audit history */}
              {Array.isArray(detail.audit) && detail.audit.length > 0 && (
                <div className="mt-3" data-testid="admin-opp-audit">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-800"><History className="h-4 w-4" /> Audit history</p>
                  <div className="mt-2 space-y-2">
                    {detail.audit.map((a, i) => (
                      <div key={i} className="rounded-lg border border-zinc-100 bg-zinc-50 p-2 text-xs text-zinc-600">
                        <span className="font-semibold capitalize text-zinc-800">{(a.kind || "approval").replace(/_/g, " ")}</span>
                        {a.from_status && a.to_status && <span> · {a.from_status} → {a.to_status}</span>}
                        {a.by_admin && <span className="ml-1 rounded bg-indigo-100 px-1 text-indigo-700">admin</span>}
                        <span className="ml-1 text-zinc-400">{fmtDate(a.at)}</span>
                        {a.reason && <p className="mt-0.5 text-zinc-600">{a.reason}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter className="mt-3">
                <Button variant="outline" data-testid="admin-opp-detail-view-applicants" onClick={() => { const d = detail; setDetail(null); setApplicantsOpp({ id: d.id, title: d.title }); }} className="rounded-full">
                  <Users className="mr-1 h-4 w-4" /> View applicants
                </Button>
                <Button variant="outline" onClick={() => setDetail(null)} className="rounded-full">Close</Button>
                <Button data-testid="admin-opp-apply-status" onClick={applyStatus} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply change"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ApplicantsDialog opp={applicantsOpp} onClose={() => setApplicantsOpp(null)}
        onInvestigate={onInvestigate ? (query) => { setApplicantsOpp(null); onInvestigate(query); } : null} />
    </div>
  );
}
